/**
 * Recent Q&A groups (spec §3): Today, Yesterday, Previous 7 days and Older,
 * by the day of the last activity in the viewer's time zone. Every Q&A is in
 * exactly one group; groups keep the list's order and empty ones are left out.
 */

export type HistoryGroup = 'today' | 'yesterday' | 'last7' | 'older';

export const GROUP_ORDER: HistoryGroup[] = [
  'today',
  'yesterday',
  'last7',
  'older',
];

export interface HistoryItem {
  id: string | number;
  name?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

/** API times are UTC; "YYYY-MM-DD HH:mm:ss" has no zone, so read it as UTC. */
export const parseApiTime = (value: string | null | undefined): number => {
  if (!value) return NaN;
  const plain = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(
    value,
  );
  return Date.parse(plain ? `${value.replace(' ', 'T')}Z` : value);
};

/** The latest of created, updated and activity seen in this session. */
export const activityTime = (
  item: HistoryItem,
  localActivity?: number,
): number => {
  const times = [
    parseApiTime(item.created_at),
    parseApiTime(item.updated_at),
    localActivity ?? NaN,
  ].filter(t => Number.isFinite(t));
  return times.length ? Math.max(...times) : NaN;
};

const startOfDay = (time: number, daysBack = 0) => {
  const d = new Date(time);
  d.setHours(0, 0, 0, 0);
  // Calendar days, so a daylight-saving change never moves a boundary.
  d.setDate(d.getDate() - daysBack);
  return d.getTime();
};

export const bucketFor = (time: number, now: number): HistoryGroup => {
  if (!Number.isFinite(time)) return 'older';
  if (time >= startOfDay(now)) return 'today';
  if (time >= startOfDay(now, 1)) return 'yesterday';
  if (time >= startOfDay(now, 7)) return 'last7';
  return 'older';
};

/** Case-, width- and kana-insensitive enough for titles: NFKC + lower case. */
const normalize = (text: string) => text.normalize('NFKC').toLocaleLowerCase();

export const matchesQuery = (item: HistoryItem, query: string): boolean => {
  const q = normalize(query.trim());
  return !q || normalize(item.name ?? '').includes(q);
};

export const groupInquiries = <T extends HistoryItem>(
  items: T[],
  now: number,
  query = '',
  localActivity: Record<string, number> = {},
): { group: HistoryGroup; items: T[] }[] => {
  const groups = new Map<HistoryGroup, T[]>(GROUP_ORDER.map(g => [g, []]));
  for (const item of items) {
    if (!matchesQuery(item, query)) continue;
    const group = bucketFor(
      activityTime(item, localActivity[String(item.id)]),
      now,
    );
    groups.get(group)!.push(item);
  }
  return GROUP_ORDER.map(group => ({
    group,
    items: groups.get(group)!,
  })).filter(g => g.items.length > 0);
};
